import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UserPlus, Check } from "lucide-react";
import api from "@/lib/api";

/**
 * „Vorschläge für dich" — NUR Social, nur Mobile (Desktop hat die rechte Spalte).
 * Freunde-von-Freunden (2. Grad) aus /me/follow-suggestions.
 */
export default function FollowSuggestionsRow({ user }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [followed, setFollowed] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user || user === false) { setLoading(false); return; }
    try {
      const { data } = await api.get("/me/follow-suggestions?limit=12");
      setItems(data.suggestions || []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const follow = async (e, s) => {
    e.stopPropagation();
    setFollowed((f) => ({ ...f, [s.profile_id]: true }));
    try {
      const { data } = await api.post(`/profiles/${s.profile_id}/follow`);
      if (data?.status === "requested") setFollowed((f) => ({ ...f, [s.profile_id]: "requested" }));
    } catch {
      setFollowed((f) => ({ ...f, [s.profile_id]: false }));
    }
  };

  if (loading || !user || user === false || items.length === 0) return null;

  return (
    <div data-testid="follow-suggestions-row" className="2xl:hidden border-b border-[#E5E5E5] bg-white py-3">
      <p className="px-4 mb-2 text-sm font-bold text-[#0A0A0A]">
        {t("suggestions.title", { defaultValue: "Vorschläge für dich" })}
      </p>
      <div className="flex gap-3 px-4 overflow-x-auto scrollbar-hide">
        {items.map((s) => {
          const state = followed[s.profile_id];
          return (
            <div
              key={s.profile_id}
              data-testid={`suggestion-${s.profile_id}`}
              onClick={() => navigate(`/user/${s.handle}`)}
              className="shrink-0 w-36 rounded-2xl border border-[#EEE] p-3 flex flex-col items-center text-center cursor-pointer hover:bg-[#FAFAFA] transition-colors"
            >
              {s.avatar_url ? (
                <img src={s.avatar_url} alt="" className="w-16 h-16 rounded-full object-cover border border-[#E5E5E5]" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-[#E5E5E5] text-[#999] flex items-center justify-center text-lg font-bold">
                  {(s.display_name || "?")[0]}
                </div>
              )}
              <p className="mt-2 text-xs font-semibold text-[#0A0A0A] truncate w-full">{s.display_name || s.handle}</p>
              {s.mutual_count > 0 && (
                <p className="text-[10px] text-[#999] truncate w-full">
                  {t(s.mutual_count === 1 ? "suggestions.mutual_one" : "suggestions.mutual_other", { n: s.mutual_count, defaultValue: `${s.mutual_count} gemeinsam` })}
                </p>
              )}
              <button
                data-testid={`suggestion-follow-${s.profile_id}`}
                onClick={(e) => follow(e, s)}
                disabled={!!state}
                className={`mt-2 h-8 w-full rounded-full text-xs font-medium flex items-center justify-center gap-1 transition-colors ${
                  state ? "bg-white border border-[#E5E5E5] text-[#0A0A0A]" : "bg-[#0A0A0A] text-white hover:bg-[#222]"
                }`}
              >
                {state === "requested"
                  ? <><Check size={13} /> {t("public_profile.requested", { defaultValue: "Angefragt" })}</>
                  : state
                  ? <><Check size={13} /> {t("public_profile.following", { defaultValue: "Folgt" })}</>
                  : <><UserPlus size={13} /> {t("suggestions.follow", { defaultValue: "Folgen" })}</>}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
